import { isEscrowAcceptedOnChain, resolveTaskStatus } from '../helpers';

const OPEN_STATUSES = new Set(['pending', 'active', 'submitted', 'disputed', 'refund_requested', 'approved']);

function isOpenTask(task) {
    const status = resolveTaskStatus(task);
    return OPEN_STATUSES.has(status);
}

function employeeIsInactive(employee) {
    return String(employee?.status || 'active').toLowerCase() === 'inactive';
}

/**
 * Action items for company dashboard — payroll blockers only.
 * @returns {{ id: string, kind: string, title: string, hint: string, task: object }[]}
 */
export function computeAttentionItems(companyTasks = [], employees = []) {
    const items = [];
    const employeeById = new Map(employees.map((e) => [String(e.id), e]));
    const seenInactiveOnTask = new Set();

    companyTasks.forEach((task) => {
        const status = resolveTaskStatus(task);
        const taskId = String(task.id);
        const title = task.title || `Task #${taskId}`;

        if (status === 'submitted') {
            items.push({
                id: `submitted-${taskId}`,
                kind: 'submitted',
                title,
                hint: 'Submitted — review and release',
                task,
            });
            return;
        }

        if (status === 'disputed') {
            items.push({
                id: `disputed-${taskId}`,
                kind: 'disputed',
                title,
                hint: 'Dispute open',
                task,
            });
            return;
        }

        if (
            task.escrowId &&
            isEscrowAcceptedOnChain(task) === false &&
            status !== 'released' &&
            status !== 'refunded'
        ) {
            items.push({
                id: `escrow-${taskId}`,
                kind: 'escrow',
                title,
                hint: 'Funded — awaiting on-chain acceptance',
                task,
            });
        }

        if (!isOpenTask(task)) return;

        const emp =
            task.employee ||
            (task.employeeId != null ? employeeById.get(String(task.employeeId)) : null);
        if (!emp || !employeeIsInactive(emp)) return;

        const key = `inactive-${taskId}`;
        if (seenInactiveOnTask.has(key)) return;
        seenInactiveOnTask.add(key);

        const name = emp.name || 'Employee';
        items.push({
            id: key,
            kind: 'inactive',
            title,
            hint: `${name} is inactive on an open task`,
            task,
        });
    });

    const priority = { submitted: 0, disputed: 1, escrow: 2, inactive: 3 };
    items.sort((a, b) => (priority[a.kind] ?? 9) - (priority[b.kind] ?? 9));

    const byTask = new Map();
    items.forEach((item) => {
        const key = String(item.task?.id ?? item.id);
        if (!byTask.has(key)) byTask.set(key, item);
    });

    return [...byTask.values()];
}
